import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useBookingDispatch,
  useBookingState,
} from "../../hooks/useBookingState";
import {
  BookingAttributeHelper,
  ProdAttrUtils,
} from "../../utils/productAttributeUtils";
import { SelectTagArea } from "../detail/SelectTagArea";
import { ProdAttrOfSimEID } from "./ProdAttrOfSimEID";

const ProdAttrOfSIM: FC<ProdAttrPickerModeProps> = (props) => {
  const booking = useBookingState(props);

  const [defaultValidity, defaultDatapackage] = useMemo(
    function () {
      const sim = booking.bookingAttributes.find(
        (x) =>
          x.productAttribute.name === "SIM" &&
          typeof x.value?.quantity === "number",
      );
      if (sim) {
        const [d, v] = (sim.productAttribute.value ?? "").split("_");
        if (d && v) {
          return [v, d];
        }
      }
      return ["", ""];
    },
    [booking],
  );
  if (!booking?.product) return null;
  return (
    <>
      <ProdAttrOfSIMBody
        key={defaultValidity + " " + defaultDatapackage}
        {...props}
        booking={booking}
        defaultValidity={defaultValidity}
        defaultDatapackage={defaultDatapackage}
      />
      <ProdAttrOfSimEID {...props} type="EID" />
      <ProdAttrOfSimEID {...props} type="IMEI" />
    </>
  );
};

const ProdAttrOfSIMBody: FC<
  ProdAttrPickerModeProps & {
    booking: BookingState;
    defaultDatapackage: string;
    defaultValidity: string;
  }
> = ({ booking, defaultDatapackage, defaultValidity, ...props }) => {
  const baHelper = useMemo(
    () => new BookingAttributeHelper(booking.bookingAttributes),
    [booking.bookingAttributes],
  );
  const { t } = useTranslation();
  const { bookingPickLs } = useBookingDispatch(props);

  const [validity, setValidity] = useState<OptionPropsValue>(defaultValidity);
  useEffect(() => {
    setValidity(defaultValidity);
  }, [defaultValidity]);

  const [dataPackage, setDataPackage] =
    useState<OptionPropsValue>(defaultDatapackage);
  useEffect(() => {
    setDataPackage(defaultDatapackage);
  }, [defaultDatapackage]);

  const isValid = booking.product?.productType === "SIM";

  const availLs = useMemo(
    () => (isValid ? baHelper.filter("SIM", "OPTIONAL") : []),
    [isValid, baHelper],
  );

  const qSimValue = useMemo(() => {
    return booking.bookingAttributes.find(
      (x) => x.productAttribute.name === "Q_SIM",
    )?.value;
  }, [booking]);

  const findAvai = useCallback(
    (v: OptionPropsValue, d: OptionPropsValue) => {
      return availLs.find((x) => x.productAttribute.value === [d, v].join("_"));
    },
    [availLs],
  );
  useEffect(() => {
    // Ráp tên lại rồi chọn
    const a = findAvai(validity, dataPackage);
    const ll = availLs.map((a) => {
      return {
        ...a,
        value: undefined,
      };
    }) as BookingAttributeDTO[];

    let applyLs;
    if (a) {
      applyLs = ll.map((x) => {
        if (a.productAttribute.id !== x.productAttribute.id) return x;
        x.value = qSimValue ?? {
          quantity: 0,
        };
        return x;
      });
    } else {
      applyLs = ll;
    }

    if (JSON.stringify(applyLs) === JSON.stringify(availLs)) return;
    console.log("SIM selected ls", applyLs);
    bookingPickLs(applyLs);
  }, [qSimValue, validity, dataPackage]); // , availLs, findAvai

  const [simValidityOption, dataPackageOptions]: OptionProps[][] = useMemo(
    function () {
      const simValidityOption: OptionProps[] = [];
      const dataPackageOptions: OptionProps[] = [];
      const mm = availLs;
      for (const x of mm) {
        const [d, v] = (x.productAttribute.value ?? "").split("_");
        if (!v || !d) continue;
        if (!dataPackageOptions.find((x) => x.value === d)) {
          dataPackageOptions.push({
            value: d,
            displayName: ProdAttrUtils.SIM.dpDataPackage(d)
              .map((x) => t(x))
              .join(" "),
          });
        }
        if (!simValidityOption.find((x) => x.value === v)) {
          simValidityOption.push({
            value: v,
            displayName: ProdAttrUtils.SIM.dpValidity(v)
              .map((x) => t(x))
              .join(" "),
          });
        }
      }
      return [simValidityOption, dataPackageOptions];
    },
    [availLs, t],
  );

  if (!isValid) {
    return null;
  }
  return (
    <>
      <SelectTagArea
        options={simValidityOption.filter((v) => {
          if (dataPackage) {
            return findAvai(v.value, dataPackage);
          }
          return true;
        })}
        defaultSelected={[validity].filter((x) => x)}
        onSelect={(options) => setValidity(options[0]?.value)}
        title={t("SIM validity")}
      />
      <SelectTagArea
        options={dataPackageOptions.filter((v) => {
          if (validity) {
            return findAvai(validity, v.value);
          }
          return true;
        })}
        defaultSelected={[dataPackage].filter((x) => x)}
        onSelect={(options) => setDataPackage(options[0]?.value)}
        title={t("Data package")}
      />
    </>
  );
};

export default ProdAttrOfSIM;
